import { useCallback, type ReactNode } from 'react'
import { type EntityId, isDefined, isNotDefined, type Preference, type PreferenceGetSuccess } from '@terros/common'
import { Tooltip, Input, Stack } from '@mantine/core'
import { useToastError } from '@hooks'
import { PREFERENCE_DEFINITIONS, usePreferenceDetail, useTranslation, useUpdatePreference } from '@front/model'
import { TAlert } from '../terros'
import { ButtonReset } from '../buttons'
import { isPreferenceVisible } from './util'
import { PreferenceSkeleton } from './PreferenceSkeleton'
import { PreferenceOverrideIndicator } from './PreferenceOverrideIndicator'
import { HistoryButton } from './HistoryButton'
import { DeprecatedCheckWrapper } from './DeprecatedCheck'
import { useMatchesPreferenceFilter, usePreferenceContext } from './Context'

type Props<T extends Preference> = {
  targetId: EntityId
  preference: T
  getDefaultLabel?: (data: PreferenceGetSuccess<T>) => string
  isLoading?: boolean
  isUpdating?: boolean
  inline?: boolean
  children: (props: { data: PreferenceGetSuccess<T>; disabled: boolean }) => ReactNode
}
export const PreferenceInput = <T extends Preference>({
  targetId,
  preference,
  getDefaultLabel,
  children,
  isLoading: isLoadingAdditionalInfo,
  isUpdating: isUpdatingPreference,
  inline = false,
}: Props<T>): ReactNode => {
  const { t } = useTranslation()
  const { isAllowedUpdate } = usePreferenceContext()
  const { data, isLoading, error } = usePreferenceDetail(preference, targetId)
  const { update, isUpdating, error: updateError } = useUpdatePreference(preference, targetId)
  useToastError(updateError)

  const definition = PREFERENCE_DEFINITIONS[preference]
  const label = t(definition.label)
  const description = definition.description ? t(definition.description) : undefined

  const setDefault = useCallback(async () => {
    await update(undefined)
  }, [update])

  const matchesSearch = useMatchesPreferenceFilter(preference)

  if (!isPreferenceVisible(targetId, preference)) return
  if (!matchesSearch) return
  if (isLoading || isLoadingAdditionalInfo) return <PreferenceSkeleton />
  if (isDefined(error))
    return (
      <TAlert variant='error' title={`Error loading preference: ${label}`}>
        {error}
      </TAlert>
    )
  if (!data) return

  return (
    <DeprecatedCheckWrapper preference={preference}>
      <div className='flex flex-row gap-4'>
        {isAllowedUpdate && (
          <Tooltip label={getDefaultTooltipLabel(data, getDefaultLabel)}>
            <ButtonReset
              disabled={data.current === undefined}
              loading={isUpdatingPreference || isUpdating}
              onClick={setDefault}
            />
          </Tooltip>
        )}
        <Stack gap='xs' flex={1} miw={0}>
          {inline ? (
            <div className='flex flex-1 flex-row items-center justify-between gap-4'>
              <div className='min-w-0 flex-1'>
                <Input.Label>{label}</Input.Label>
                {description && <Input.Description>{description}</Input.Description>}
              </div>
              <div className='flex-shrink-0'>{children({ data, disabled: !isAllowedUpdate })}</div>
            </div>
          ) : (
            <Input.Wrapper description={description} label={label} flex={1}>
              <div className='mt-1'>{children({ data, disabled: !isAllowedUpdate })}</div>
            </Input.Wrapper>
          )}
          <PreferenceOverrideIndicator targetId={targetId} preference={preference} current={data.current} />
        </Stack>
        <HistoryButton data={data} preferenceId={preference} />
      </div>
    </DeprecatedCheckWrapper>
  )
}

const getDefaultTooltipLabel = <T extends Preference>(
  data: PreferenceGetSuccess<T>,
  getDefaultLabel?: (data: PreferenceGetSuccess<T>) => string
): string => {
  if (isNotDefined(getDefaultLabel)) {
    return 'Reset to default'
  }
  const label = getDefaultLabel(data)
  return `Reset to default (${label})`
}
